const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Keeps the end date in the same relative range when the start date moves to
 * another calendar month. For changes inside the same month the end date is
 * left untouched unless it would become earlier than the new start date.
 */
export function syncRangeEndWithStartMonth(
  previousStart: string | null | undefined,
  currentEnd: string | null | undefined,
  nextStart: string | null | undefined
): string | null {
  const next = parseDateOnly(nextStart);
  if (!next) {
    return currentEnd ?? null;
  }

  const previous = parseDateOnly(previousStart);
  const end = parseDateOnly(currentEnd);

  if (!previous || !end) {
    return end && end.getTime() >= next.getTime()
      ? formatDateOnly(end)
      : formatDateOnly(next);
  }

  const monthChanged = previous.getUTCFullYear() !== next.getUTCFullYear()
    || previous.getUTCMonth() !== next.getUTCMonth();

  if (monthChanged) {
    const rangeLengthDays = Math.max(0, Math.round((end.getTime() - previous.getTime()) / DAY_MS));
    const shiftedEnd = new Date(next.getTime() + rangeLengthDays * DAY_MS);
    return formatDateOnly(shiftedEnd);
  }

  return end.getTime() >= next.getTime()
    ? formatDateOnly(end)
    : formatDateOnly(next);
}

function parseDateOnly(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value?.trim() ?? '');
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
      ? date
      : null;
}

function formatDateOnly(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}
